'use client';

import React, { useState, useRef, useCallback, useEffect } from 'react';
import fileManagerServices from '../../utils/services/fileManager';
import assetServices from '../../utils/services/asset';
import { useIdPath } from '@/contexts/IdPathProvider';
import flipbookService from '@/templates/Admin/utils/services/flipbook';

interface FileUploaderProps {
  selectedAssetType: { id: number; name: string } | null;
  currentCategoryUuid?: string; // Current category UUID for asset placement
  onUploadComplete: () => void;
  className?: string;
}

interface UploadProgress {
  fileName: string;
  progress: number;
  status: 'uploading' | 'creating-asset' | 'completed' | 'error';
  error?: string;
}

export default function FileUploader({ selectedAssetType, currentCategoryUuid, onUploadComplete, className }: FileUploaderProps) {
  const { idPath, namePath } = useIdPath();
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Debug log for context values
  useEffect(() => {
    console.log('FileUploader context values:', { idPath, namePath });
  }, [idPath, namePath]);

  // Determine file category and folder path based on file type
  const getFileCategory = (file: File): { category: string; folderPath: string } => {
    const extension = file.name.toLowerCase().split('.').pop() || '';

    // Model files
    if (['glb', 'gltf', 'obj', 'fbx', '3ds', 'dae', 'blend'].includes(extension)) {
      return { category: 'model', folderPath: 'model' };
    }

    // Image files
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) {
      return { category: 'hien-vat-anh', folderPath: 'hien-vat-anh' };
    }

    // Video files
    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension)) {
      return { category: 'video', folderPath: 'video' };
    }

    // Audio files
    if (['mp3', 'wav', 'ogg', 'aac', 'flac'].includes(extension)) {
      return { category: 'audio', folderPath: 'audio' };
    }

    // Document files (including ZIP for flipbook)
    if (['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'rtf', 'odt', 'ods', 'odp', 'zip'].includes(extension)) {
      return { category: 'document', folderPath: 'document' };
    }

    // Default to document
    return { category: 'document', folderPath: 'document' };
  };

  // Get asset type ID based on category
  const getAssetTypeId = (category: string): number => {
    // Use fixed mapping as requested
    switch (category) {
      case 'model': return 1;
      case 'hien-vat-anh': return 2;
      case 'video': return 4;
      case 'audio': return 5;
      case 'document': return 3; // Document
      default: return 3; // Default to document
    }
  };

  // Upload single file and create asset
  const uploadFileAndCreateAsset = async (file: File, index: number) => {
    const { category, folderPath } = getFileCategory(file);
    const cleanNamePath = namePath?.replace(/\/admin\/?$/, '') || '';
    const folderUrl = `${cleanNamePath}/upload/${folderPath}`;


    try {
      // Update progress: uploading
      setUploadProgress(prev => prev.map((item, i) =>
        i === index ? { ...item, status: 'uploading', progress: 25 } : item
      ));


      // Upload file to S3
      const uploadResponse = await fileManagerServices.uploadFiles({
        folderUrl,
        files: [file]
      }, idPath);

      // Check if upload was successful
      if (!uploadResponse) {
        console.error('Upload failed - No response received');
        throw new Error('Upload failed - No response from server');
      }

      if (!uploadResponse.success && !uploadResponse.data) {
        console.error('Upload failed - Invalid response structure:', uploadResponse);
        throw new Error(`Upload failed - Server error: ${uploadResponse.error || 'Unknown error'}`);
      }

      // Extract real file URL from upload response
      let realFileUrl = '';

      // Try to get the real URL from different possible response structures
      if (uploadResponse.data) {
        // Case 1: Response has data.files array with uploaded files info
        if (uploadResponse.data.success) {
          const uploadedFile = uploadResponse.data;
          if (uploadedFile && uploadedFile.file_url) {
            realFileUrl = uploadedFile.file_url;
          }
        }

        // Case 2: Response has data.fullUrl directly
        if (!realFileUrl && uploadResponse.data.fullUrl) {
          realFileUrl = uploadResponse.data.fullUrl;
        }

        // Case 3: Response has data.url
        if (!realFileUrl && uploadResponse.data.url) {
          realFileUrl = uploadResponse.data.url;
        }
      }

      // Case 4: Response has success and url directly
      if (!realFileUrl && uploadResponse.url) {
        realFileUrl = uploadResponse.url;
      }

      // Fallback to constructed URL if no real URL found
      if (!realFileUrl) {
        realFileUrl = `${folderUrl}${file.name}`;
        console.warn('No real URL found in upload response, using fallback:', realFileUrl);
        console.warn('Upload response structure:', JSON.stringify(uploadResponse, null, 2));
      }


      // Update progress: getting form data
      setUploadProgress(prev => prev.map((item, i) =>
        i === index ? { ...item, status: 'uploading', progress: 50 } : item
      ));

      // Get asset type ID and form data
      const assetTypeId = getAssetTypeId(category);

      // Get form structure to get correct variable IDs
      const formResponse = await assetServices.getFormAssetByType({
        id_path: idPath || '',
        type: assetTypeId.toString()
      }) as any;


      // Update progress: creating asset
      setUploadProgress(prev => prev.map((item, i) =>
        i === index ? { ...item, status: 'creating-asset', progress: 75 } : item
      ));

      // Get file name without extension for asset title
      const fileName = file.name.replace(/\.[^/.]+$/, '');
      const fileUrl = realFileUrl; // Use the real URL from S3 response

      // Build attributes object with correct IDs from form data
      const attributes: any = {};

      if (formResponse?.success && formResponse.data?.variables) {
        const variables = formResponse.data.variables;

        // Find variable IDs by name
        const titleVnVar = variables.find((v: any) => v.asset_variable_name === 'title_vn');
        const titleEnVar = variables.find((v: any) => v.asset_variable_name === 'title_en');
        const thumbnailVar = variables.find((v: any) => v.asset_variable_name === 'thumbnail');
        const model3dVar = variables.find((v: any) => v.asset_variable_name === 'model3d');
        const videoVar = variables.find((v: any) => v.asset_variable_name === 'video');
        const audioVar = variables.find((v: any) => v.asset_variable_name === 'audio');
        // Find document related variables
        const documentVar = variables.find((v: any) =>
          v.asset_variable_name.includes('document') ||
          v.asset_variable_name.includes('tai_lieu') ||
          v.asset_variable_name.includes('file')
        );

        // Add common attributes
        if (titleVnVar) {
          attributes.title_vn = { id: titleVnVar.id, values: fileName };
        }
        if (titleEnVar) {
          attributes.title_en = { id: titleEnVar.id, values: fileName };
        }
        if (thumbnailVar) {
          attributes.thumbnail = {
            id: thumbnailVar.id,
            values: category === 'hien-vat-anh' ? fileUrl : ''
          };
        }

        // Add file-specific attributes using the real S3 URL
        if (category === 'model' && model3dVar) {
          attributes.model3d = { id: model3dVar.id, values: fileUrl };
        }
        if (category === 'video' && videoVar) {
          attributes.video = { id: videoVar.id, values: fileUrl };
        }
        if (category === 'audio' && audioVar) {
          attributes.audio = { id: audioVar.id, values: fileUrl };
        }
        if (category === 'document' && documentVar) {
          attributes[documentVar.asset_variable_name] = { id: documentVar.id, values: fileUrl };
        }
        if (category === 'hien-vat-anh' && thumbnailVar) {
          // For images, the main file URL goes to thumbnail
          attributes.thumbnail = { id: thumbnailVar.id, values: fileUrl };
        }

      } else {
        // Fallback to default IDs if form data not available
        console.warn('Using fallback attribute IDs - form data not available');
        attributes.title_vn = { id: 1, values: fileName };
        attributes.title_en = { id: 2, values: fileName };
        attributes.thumbnail = { id: 3, values: category === 'hien-vat-anh' ? fileUrl : '' };

        if (category === 'model') {
          attributes.model3d = { id: 4, values: fileUrl };
        }
        if (category === 'video') {
          attributes.video = { id: 5, values: fileUrl };
        }
        if (category === 'audio') {
          attributes.audio = { id: 6, values: fileUrl };
        }
        if (category === 'document') {
          // Guessing a document ID if fallback is needed, though not ideal
          attributes.document = { id: 7, values: fileUrl };
        }
      }

      // Create asset body
      const assetBody = {
        asset_type_id: assetTypeId,
        category_uuid: currentCategoryUuid || '', // Add current category UUID
        attributes: attributes
      };


      const createAssetResponse = await assetServices.createNewAsset(idPath || '', {
        body: assetBody
      });

      // If this is a ZIP file (flipbook), call the flipbook upload API
      const fileExtension = file.name.toLowerCase().split('.').pop() || '';
      if (fileExtension === 'zip' && createAssetResponse?.data?.uuid) {
        try {
          const flipbookResponse = await flipbookService.uploadFlipbook(
            createAssetResponse.data.uuid,
            file,
            idPath || ''
          );

          if (flipbookResponse.success) {
            // Auto-enable flipbook after successful upload
            try {
              const toggleResponse = await flipbookService.toggleFlipbook(
                createAssetResponse.data.uuid,
                true, // enable = true
                idPath || ''
              );

              if (!toggleResponse.success) {
                console.warn('Failed to enable flipbook:', toggleResponse.message);
              }
            } catch (toggleError) {
              console.error('Error enabling flipbook:', toggleError);
            }
          } else {
            console.warn('Flipbook upload failed:', flipbookResponse.message);
          }
        } catch (flipbookError) {
          console.error('Error uploading flipbook:', flipbookError);
          // Don't fail the entire upload if flipbook processing fails
        }
      }

      // Update progress: completed
      setUploadProgress(prev => prev.map((item, i) =>
        i === index ? { ...item, status: 'completed', progress: 100 } : item
      ));

    } catch (error) {
      console.error(`Error uploading ${file.name}:`, error);
      setUploadProgress(prev => prev.map((item, i) =>
        i === index ? {
          ...item,
          status: 'error',
          progress: 0,
          error: error instanceof Error ? error.message : 'Upload failed'
        } : item
      ));
    }
  };

  // Handle file selection/drop
  const handleFiles = async (files: FileList | File[]) => {
    if (!files.length) {
      return;
    }

    const fileArray = Array.from(files);

    setIsUploading(true);

    // Initialize progress tracking
    const initialProgress = fileArray.map(file => ({
      fileName: file.name,
      progress: 0,
      status: 'uploading' as const
    }));
    setUploadProgress(initialProgress);

    // Upload all files sequentially (not parallel) for better debugging
    for (let i = 0; i < fileArray.length; i++) {
      await uploadFileAndCreateAsset(fileArray[i], i);
    }

    setIsUploading(false);
    onUploadComplete();

    // Clear progress after 5 seconds (increased for debugging)
    setTimeout(() => {
      setUploadProgress([]);
    }, 5000);
  };

  // Drag and drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    handleFiles(files);
  }, []);

  // File input change handler
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      handleFiles(files);
    }
  };

  return (
    <div className={className}>
      {/* File Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-2 text-center transition-colors ${isDragging
          ? 'border-blue-500 bg-blue-50'
          : 'border-gray-300 hover:border-gray-400'
          } ${isUploading ? 'pointer-events-none opacity-50' : 'cursor-pointer'}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="flex flex-col items-center gap-2">
          <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>

          <div>
            <p className="text-lg font-medium text-gray-900">
              {isDragging ? 'Thả tệp để tải lên' : 'Kéo thả tệp hoặc nhấn để chọn'}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Hỗ trợ: Model (.glb, .gltf, .obj), Hình ảnh (.jpg, .png), Video (.mp4, .mov), Document (.pdf, .docx, .xlsx, .zip)
            </p>
            <p className="text-xs text-blue-600 italic mt-1">
              (Dung lượng khuyến nghị cho mỗi file: dưới 1MB)
            </p>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileInputChange}
          accept=".glb,.gltf,.obj,.fbx,.jpg,.jpeg,.png,.gif,.mp4,.avi,.mov,.mp3,.wav,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.odt,.ods,.odp,.zip"
        />
      </div>

      {/* Upload Progress */}
      {uploadProgress.length > 0 && (
        <div className="mt-4 space-y-3">
          <h3 className="text-lg font-medium text-gray-900">Tiến trình tải lên</h3>

          {uploadProgress.map((item, index) => (
            <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-900 truncate">
                  {item.fileName}
                </span>
                <span className={`text-xs px-2 py-1 rounded-full ${item.status === 'completed' ? 'bg-green-100 text-green-800' :
                  item.status === 'error' ? 'bg-red-100 text-red-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                  {item.status === 'uploading' ? 'Đang tải lên...' :
                    item.status === 'creating-asset' ? 'Tạo hiện vật...' :
                      item.status === 'completed' ? 'Hoàn thành' :
                        'Lỗi'}
                </span>
              </div>

              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-300 ${item.status === 'completed' ? 'bg-green-500' :
                    item.status === 'error' ? 'bg-red-500' :
                      'bg-blue-500'
                    }`}
                  style={{ width: `${item.progress}%` }}
                />
              </div>

              {item.error && (
                <p className="text-xs text-red-600 mt-2">{item.error}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
